// ./Comp/Box1Graph.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import Classes from "./Hero2.module.css";
import backend from "../../../../../../../api";
import { useSelector } from "react-redux";

/* ───── Placeholder (fallback) data ───── */
const DATA = {
  year: [
    { label: "Jan", revenue: 12000, clients: 82, appointments: 45 },
    { label: "Feb", revenue: 15500, clients: 95, appointments: 52 },
    { label: "Mar", revenue: 16750, clients: 101, appointments: 55 },
    { label: "Apr", revenue: 14200, clients: 88, appointments: 49 },
    { label: "May", revenue: 17800, clients: 110, appointments: 61 },
    { label: "Jun", revenue: 19200, clients: 118, appointments: 67 },
    { label: "Jul", revenue: 21500, clients: 130, appointments: 72 },
    { label: "Aug", revenue: 22450, clients: 136, appointments: 75 },
    { label: "Sep", revenue: 20100, clients: 122, appointments: 69 },
    { label: "Oct", revenue: 22800, clients: 140, appointments: 78 },
    { label: "Nov", revenue: 23500, clients: 145, appointments: 81 },
    { label: "Dec", revenue: 26000, clients: 158, appointments: 88 },
  ],
  month: Array.from({ length: 30 }).map((_, i) => {
    const day = i + 1;
    const base = 400 + i * 20;
    return {
      label: `Day ${day}`,
      revenue: base + (i % 5) * 30 + (i % 3) * 10,
      clients: 10 + (i % 7) + Math.round(i / 6),
      appointments: 6 + (i % 5),
    };
  }),
  week: [
    { label: "Mon", revenue: 520, clients: 15, appointments: 8 },
    { label: "Tue", revenue: 610, clients: 17, appointments: 9 },
    { label: "Wed", revenue: 680, clients: 20, appointments: 10 },
    { label: "Thu", revenue: 590, clients: 18, appointments: 9 },
    { label: "Fri", revenue: 740, clients: 23, appointments: 12 },
    { label: "Sat", revenue: 810, clients: 25, appointments: 13 },
    { label: "Sun", revenue: 460, clients: 12, appointments: 6 },
  ],
  day: [
    { label: "6 AM", revenue: 80, clients: 2, appointments: 1 },
    { label: "9 AM", revenue: 160, clients: 4, appointments: 2 },
    { label: "12 PM", revenue: 220, clients: 6, appointments: 3 },
    { label: "3 PM", revenue: 260, clients: 7, appointments: 3 },
    { label: "6 PM", revenue: 310, clients: 9, appointments: 4 },
    { label: "9 PM", revenue: 200, clients: 5, appointments: 2 },
  ],
};

const METRIC_LABELS = {
  revenue: "Revenue",
  clients: "Clients",
  appointments: "Appointments",
};

const RANGE_LABELS = {
  year: "This year",
  month: "Monthly",
  week: "Weekly",
  day: "Daily",
};

export default function Box1Graph() {
  const [metric, setMetric] = useState("revenue"); // 'revenue' | 'clients' | 'appointments'
  const [range, setRange] = useState("year"); // 'year' | 'month' | 'week' | 'day'
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState(DATA[range] || []);
  const onboarding = useSelector((s) => s.companyInfo.info);
  const companyId = onboarding?._id;

  const [openMetric, setOpenMetric] = useState(false);
  const [openRange, setOpenRange] = useState(false);
  const ddWrapRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!ddWrapRef.current) return;
      if (!ddWrapRef.current.contains(e.target)) {
        setOpenMetric(false);
        setOpenRange(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Fetch timeseries whenever range/company changes
useEffect(() => {
  let alive = true;
  (async () => {
    if (!companyId) { setSeries(DATA[range] || []); return; }
    try {
      setLoading(true);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const { data } = await backend.get("/api/admin/stats/timeseries", {
        params: { companyId, range, tz },
      });
      if (alive && data?.ok) setSeries(data.points || []);
      else if (alive) setSeries(DATA[range] || []);
    } catch (e) {
      console.error("timeseries fetch err:", e);
      if (alive) setSeries(DATA[range] || []);
    } finally {
      if (alive) setLoading(false);
    }
  })();
  return () => { alive = false; };
}, [companyId, range]);


  // y-axis formatter varies by metric
  const numberFmt = useMemo(
    () => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }),
    []
  );
  const currencyFmt = useMemo(
    () => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }),
    []
  );

  const yFormatter = (v) => (metric === "revenue" ? currencyFmt.format(v) : numberFmt.format(v));
  const tooltipFormatter = (value) =>
    metric === "revenue" ? [currencyFmt.format(value), METRIC_LABELS[metric]] : [numberFmt.format(value), METRIC_LABELS[metric]];

  const areaId = "revAreaGradient";

  return (
    <div className={Classes.boxInner}>
      {/* Header with filters */}
      <div className={Classes.header}>
        <h5>{`Total ${METRIC_LABELS[metric]}`}{loading ? " …" : ""}</h5>

        <div className={Classes.filters} ref={ddWrapRef}>
          {/* Metric dropdown */}
          <div className={Classes.dropdown}>
            <button
              type="button"
              className={Classes.dropdownBtn}
              onClick={() => {
                setOpenMetric((p) => !p);
                setOpenRange(false);
              }}
            >
              {METRIC_LABELS[metric]} <span className={Classes.caret}>▾</span>
            </button>
            {openMetric && (
              <ul className={Classes.menu}>
                {["revenue", "clients", "appointments"].map((key) => (
                  <li
                    key={key}
                    className={`${Classes.menuItem} ${metric === key ? Classes.activeItem : ""}`}
                    onClick={() => {
                      setMetric(key);
                      setOpenMetric(false);
                    }}
                  >
                    {METRIC_LABELS[key]}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Range dropdown */}
          <div className={Classes.dropdown}>
            <button
              type="button"
              className={Classes.dropdownBtn}
              onClick={() => {
                setOpenRange((p) => !p);
                setOpenMetric(false);
              }}
            >
              {RANGE_LABELS[range]} <span className={Classes.caret}>▾</span>
            </button>
            {openRange && (
              <ul className={Classes.menu} style={{ right: 0 }}>
                {["year", "month", "week", "day"].map((key) => (
                  <li
                    key={key}
                    className={`${Classes.menuItem} ${range === key ? Classes.activeItem : ""}`}
                    onClick={() => {
                      setRange(key);
                      setOpenRange(false);
                    }}
                  >
                    {RANGE_LABELS[key]}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Optional legend/badge */}
      <div className={Classes.legendRow}>
        <span className={Classes.legendDot} />
        <span className={Classes.legendLabel}>{METRIC_LABELS[metric]}</span>
      </div>

      {/* Chart */}
      <div className={Classes.chartWrap}>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--primary-color)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="var(--primary-color)" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(0,0,0,0.06)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "rgba(0,0,0,0.55)" }} />
            <YAxis tickFormatter={yFormatter} tick={{ fontSize: 11, fill: "rgba(0,0,0,0.55)" }} width={70} />
            <Tooltip
              formatter={tooltipFormatter}
              labelClassName={Classes.tooltipLabel}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                fontSize: '13px',
              }}
            />
            <Area
              type="monotone"
              dataKey={metric}
              stroke="var(--primary-color)"
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#${areaId})`}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
